// @ts-nocheck
import React from 'react';
import { motion } from 'framer-motion';
import { Video } from '../types';
import VideoCard from './VideoCard';
import { Trash2 } from 'lucide-react'; // Importar el icono de la papelera
import { VideoCardSkeleton } from './Skeletons';
import { Link } from 'react-router-dom';
import { useToast } from './ToastProvider';
import { useSettings } from './SettingsContext';
import { getUserLocation, distanceKmToQuery, formatDistanceLocalized } from '../utils/geo';
import CompareDrawer from './CompareDrawer';

interface VideoGridProps {
  videos: Video[];
  onVideoClick: (video: Video) => void;
  onDeleteVideo: (videoId: string) => void; // Nueva prop para manejar la eliminación
  isLoading: boolean;
  currentUserId?: string | null;
  onAddVideo?: (url: string) => Promise<void> | void;
  highlightedVideoId?: string | null;
  registerItemRef?: (id: string, el: HTMLElement | null) => void;
}

const VideoGrid: React.FC<VideoGridProps> = ({ videos, onVideoClick, onDeleteVideo, isLoading, currentUserId, onAddVideo, highlightedVideoId, registerItemRef }) => {
  const { toast } = useToast();
  const { settings } = useSettings();
  const gridGapClass = settings.density === 'compact' ? 'gap-3 mt-4' : 'gap-6 mt-8';
  const motionDuration = settings.reduce_motion ? 0.15 : 0.5;

  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set());
  const [userLoc, setUserLoc] = React.useState<{ lat: number; lng: number } | null>(null);
  const [distances, setDistances] = React.useState<Record<string, { km: number; label: string }>>({});
  const [showCompare, setShowCompare] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    const t = setTimeout(() => {
      getUserLocation().then((loc) => { if (!cancelled) setUserLoc(loc); });
    }, 1500); // delay slightly to avoid login flicker
    return () => { cancelled = true; clearTimeout(t); };
  }, []);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!userLoc) return;
    const next: Record<string, { km: number; label: string }> = {};
      const slice = videos.slice(0, 40);
  for (const v of slice) {
        if (!v.id) continue;
        const q = [v.restaurant_address !== 'N/A' ? v.restaurant_address : '', v.restaurant_name !== 'N/A' ? v.restaurant_name : '', v.restaurant_location !== 'N/A' ? v.restaurant_location : ''].filter(Boolean).join(' ');
        if (!q) continue;
        try {
  const km = await distanceKmToQuery(userLoc, q);
  if (km != null) next[v.id] = { km, label: formatDistanceLocalized(km, settings?.language === 'en' ? 'en-US' : 'es-ES')! };
        } catch {}
        if (cancelled) return;
      }
      if (!cancelled) setDistances(next);
    })();
    return () => { cancelled = true; };
  }, [userLoc, videos]);

  const toggleSelect = (id: string, next: boolean) => {
    setSelectedIds((prev) => {
      const copy = new Set(prev);
      next ? copy.add(id) : copy.delete(id);
      return copy;
    });
  };
  if (isLoading) {
    const count = 12;
    return (
      <div className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 ${gridGapClass}`}>
        {Array.from({ length: count }).map((_, i) => (
          <VideoCardSkeleton key={i} index={i} />
        ))}
      </div>
    );
  }

  if (videos.length === 0) {
    const canAdd = typeof onAddVideo === 'function';
    const pasteOnce = async () => {
      if (!canAdd) return;
      try {
        const text = await navigator.clipboard.readText();
        const value = (text || '').trim();
        if (!/^https?:\/\//i.test(value)) {
          toast('El portapapeles no contiene un enlace válido.', 'info');
          return;
        }
        await onAddVideo!(value);
      } catch (e) {
        toast('No se pudo leer del portapapeles.', 'error');
      }
    };
    const importFromClipboard = async () => {
      if (!canAdd) return;
      try {
        const text = await navigator.clipboard.readText();
        const urls = Array.from(new Set((text.match(/https?:\/\/\S+/g) || []).map(u => u.replace(/[),.]+$/, ''))));
        if (urls.length === 0) {
          toast('No se encontraron enlaces en el portapapeles.', 'info');
          return;
        }
        const MAX = 5;
        const slice = urls.slice(0, MAX);
        toast(`Importando ${slice.length} enlace(s)...`, 'info');
        for (const u of slice) {
          await onAddVideo!(u);
        }
        if (urls.length > MAX) toast(`Se ignoraron ${urls.length - MAX} enlaces adicionales.`, 'info');
      } catch (e) {
        toast('No se pudo leer del portapapeles.', 'error');
      }
    };

    return (
      <div className="mt-16">
        <div className="mx-auto max-w-xl text-center bg-white/70 backdrop-blur rounded-2xl p-6 shadow-sm">
          <h3 className="text-xl font-semibold text-gray-900">Aún no hay videos</h3>
          <p className="text-gray-600 mt-1">Prueba una de estas opciones para empezar:</p>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
            <button
              onClick={pasteOnce}
              disabled={!canAdd}
              className="px-3 py-2 rounded-lg bg-blue-600 text-white text-sm disabled:opacity-50 hover:bg-blue-700"
              aria-disabled={!canAdd}
            >
              Pegar enlace
            </button>
            <Link
              to="/find"
              className="px-3 py-2 rounded-lg bg-green-600 text-white text-sm text-center hover:bg-green-700"
            >
              Buscar cerca
            </Link>
            <button
              onClick={importFromClipboard}
              disabled={!canAdd}
              className="px-3 py-2 rounded-lg bg-gray-900 text-white text-sm disabled:opacity-50 hover:bg-gray-800"
              aria-disabled={!canAdd}
            >
              Importar del portapapeles
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2">Consejo: también puedes usar la barra de búsqueda superior o el botón flotante en móvil.</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 ${gridGapClass} overflow-visible`}
      initial={false}
      animate={false}
    >
      {/* Compare bar */}
  {selectedIds.size > 0 && (
        <div className="col-span-full -mt-4 mb-2 flex items-center justify-between bg-white/80 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-800 rounded-xl p-2">
          <div className="text-sm">{selectedIds.size} selected</div>
          <button
            className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700"
    onClick={() => setShowCompare(true)}
          >Compare</button>
        </div>
      )}
      {videos.map((video, idx) => {
        const isOwner = Boolean(currentUserId && video.user_id && currentUserId === video.user_id);
        const isHighlighted = Boolean(highlightedVideoId && video.id && highlightedVideoId === video.id);
    const distObj = video.id ? distances[video.id] : undefined;
        return (
      <div
            key={video.id}
            ref={(el) => video.id && registerItemRef?.(video.id, el)}
            className={
        `relative group rounded-xl overflow-visible ${isHighlighted ? 'ring-4 ring-blue-400 ring-offset-2 ring-offset-transparent' : ''}`
            }
          >
            <VideoCard
      video={{ ...video, distance_label: distObj?.label, distance_km: distObj?.km } as any}
              onVideoClick={onVideoClick}
              index={idx}
              onToggleSelect={(id, next)=>toggleSelect(id, next)}
              selected={Boolean(video.id && selectedIds.has(video.id))}
              userLatLng={userLoc}
              directionsPref={settings.directions_pref}
              onDeleteVideo={onDeleteVideo}
            />
            {/* Owner delete FAB removed; use overflow menu inside the card */}
          </div>
        );
      })}
      {/* Compare drawer */}
      {showCompare && (
        <CompareDrawer
          videos={videos.filter(v => v.id && selectedIds.has(v.id)) as any[]}
          onClose={() => setShowCompare(false)}
        />
      )}
    </motion.div>
  );
};


export default VideoGrid;